import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { apiClient } from '../../services/api';
import { GlassButton } from '../ui/GlassButton';
import './VerifyEmailPage.css';

type State = 'verifying' | 'success' | 'already_verified' | 'error';

const VerifyEmailPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [state, setState] = useState<State>('verifying');
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            setState('error');
            setErrorMsg('No verification token found. Please use the link from your email.');
            return;
        }

        apiClient
            .get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
            .then((res) => {
                setState(res.data.already_verified ? 'already_verified' : 'success');
            })
            .catch((err) => {
                setState('error');
                setErrorMsg(
                    err?.response?.data?.detail ||
                    'This verification link is invalid or has expired. Please request a new one.'
                );
            });
    }, [searchParams]);

    const goToDashboard = () => {
        // Force a full reload so AuthContext re-fetches the user with email_verified=true
        window.location.href = '/dashboard';
    };

    return (
        <div className="verify-email-page">
            <div className="verify-email-card">
                {state === 'verifying' && (
                    <>
                        <div className="verify-email-icon spinning">
                            <Loader size={32} strokeWidth={1.5} />
                        </div>
                        <h2>Verifying your email…</h2>
                        <p>Just a moment.</p>
                    </>
                )}

                {(state === 'success' || state === 'already_verified') && (
                    <>
                        <div className="verify-email-icon success">
                            <CheckCircle size={32} strokeWidth={1.5} />
                        </div>
                        <h2>
                            {state === 'already_verified'
                                ? 'Email already verified'
                                : 'Email verified!'}
                        </h2>
                        <p>
                            {state === 'already_verified'
                                ? 'Your email address is already verified. You can continue to the dashboard.'
                                : 'Your email address has been confirmed. Welcome to AIME!'}
                        </p>
                        <GlassButton variant="primary" onClick={goToDashboard} className="verify-email-btn">
                            Go to Dashboard
                        </GlassButton>
                    </>
                )}

                {state === 'error' && (
                    <>
                        <div className="verify-email-icon error">
                            <XCircle size={32} strokeWidth={1.5} />
                        </div>
                        <h2>Verification failed</h2>
                        <p>{errorMsg}</p>
                        <GlassButton
                            variant="secondary"
                            onClick={() => navigate('/login')}
                            className="verify-email-btn"
                        >
                            Back to login
                        </GlassButton>
                    </>
                )}
            </div>
        </div>
    );
};

export default VerifyEmailPage;
